import Siema from 'siema';
import pushid from 'pushid';
import ResizeObserver from 'resize-observer-polyfill';
import Helper from '../helpers/Helper';

export default class Slider {
    constructor() {
        this.el = null;
        this.slider = null;
        this.startIndex = 0;
        this.dragging = null;
        this.firstSetup = true;
        this.interval = null;
    }
    init(el) {
        if (el === undefined || el === null) {
            return;
        }
        this.el = el;
        this.initSlider();
        this.setupResizeObserver();
        this.updateHeightOnFrontend();
    }
    initSlider() {
        let original = this.el.children[0],
            copy = null;

        /* if slider class is not set, layout has been changed: kill old instance */
        if (!this.el.classList.contains('slider')) {
            this.killSlider();
            original.classList.remove('slider__instance');
            original.style.display = 'block';
            return;
        }

        /* add classes */
        original.classList.add('slider__instance');

        /* save event listeners */
        if (original.querySelector('a') !== null) {
            [].forEach.call(original.querySelectorAll('a'), (el) => {
                if (!el.classList.contains('saved-trigger')) {
                    el.classList.add('saved-trigger');
                    el.classList.add('saved-trigger--' + pushid());
                }
            });
        }

        /* kill old instance on update in editor */
        this.killSlider();

        /* create copy */
        copy = original.cloneNode(true);
        original.style.display = 'none';
        copy.style.display = 'block';
        copy.classList.add('slider__instance--copy');
        original.parentNode.insertBefore(copy, original.nextSibling);

        /* init new event listeners */
        if (copy.querySelector('a') !== null) {
            [].forEach.call(copy.querySelectorAll('a'), (el) => {
                // fix lightgallery in slider
                if (el.classList.contains('lightgallery')) {
                    el.classList.remove('lightgallery');
                }
                // fix mouse dragging
                el.addEventListener('click', (e) => {
                    if (Math.abs(e.clientX - this.dragging) < 3) {
                        original.querySelector('.' + el.className.split(' ').join('.')).click();
                    }
                    e.preventDefault();
                });
            });
        }

        Helper.fixWshSvgs();

        /* finally initialize slider */
        this.initializeSlider(copy);

        /* build buttons */
        this.buildButtons();

        /* build thumbnail buttons */
        this.buildThumbnailButtons();

        /* build thumbnail bar */
        this.buildThumbnailBar();

        /* build dots */
        this.buildDots();

        /* init autoplay */
        const isReduced =
            window.matchMedia(`(prefers-reduced-motion: reduce)`) === true ||
            window.matchMedia(`(prefers-reduced-motion: reduce)`).matches === true;

        if (isReduced) {
            // DON'T use an amination here!
        } else {
            this.initAutoplay();
            // DO use an animation here!
        }

        /* set height */
        this.setHeight();
    }
    getPrevThumbnailSrc() {
        let prevThumbnailSrc = '',
            prevThumbnailIndex = this.getCurrentIndex() - 1;
        if (this.getSlideCount() >= 1) {
            if (this.getCurrentIndex() === 0 && this.hasLoop()) {
                prevThumbnailSrc = this.el.querySelector(
                    '.imageslider__slider-items.slider__instance .imageslider__item:last-child .image-container'
                ).dataset.thumbnail;
            } else if (
                this.el.querySelector(
                    '.imageslider__slider-items.slider__instance .imageslider__item:nth-child(' +
                        (prevThumbnailIndex + 1) +
                        ') .image-container'
                ) !== null
            ) {
                prevThumbnailSrc = this.el.querySelector(
                    '.imageslider__slider-items.slider__instance .imageslider__item:nth-child(' +
                        (prevThumbnailIndex + 1) +
                        ') .image-container'
                ).dataset.thumbnail;
            }
        }
        return prevThumbnailSrc;
    }
    getNextThumbnailSrc() {
        let nextThumbnailSrc = '',
            nextThumbnailIndex = this.getCurrentIndex() + 1;
        if (this.getSlideCount() >= 1) {
            if (
                this.getCurrentIndex() === this.getSlideCount() - this.getSlidesPerPage() &&
                this.hasLoop()
            ) {
                nextThumbnailSrc = this.el.querySelector(
                    '.imageslider__slider-items.slider__instance .imageslider__item:first-child .image-container'
                ).dataset.thumbnail;
            } else if (
                this.el.querySelector(
                    '.imageslider__slider-items.slider__instance .imageslider__item:nth-child(' +
                        (nextThumbnailIndex + 1) +
                        ') .image-container'
                ) !== null
            ) {
                nextThumbnailSrc = this.el.querySelector(
                    '.imageslider__slider-items.slider__instance .imageslider__item:nth-child(' +
                        (nextThumbnailIndex + 1) +
                        ') .image-container'
                ).dataset.thumbnail;
            }
        }
        return nextThumbnailSrc;
    }
    getThumbnailSrc(i) {
        let nextThumbnailSrc = '',
            nextThumbnailIndex = i;
        if (
            this.el.querySelector(
                '.imageslider__slider-items.slider__instance .imageslider__item:nth-child(' +
                    (nextThumbnailIndex + 1) +
                    ') .image-container'
            ) !== null
        ) {
            nextThumbnailSrc = this.el.querySelector(
                '.imageslider__slider-items.slider__instance .imageslider__item:nth-child(' +
                    (nextThumbnailIndex + 1) +
                    ') .image-container'
            ).dataset.thumbnail;
        }
        return nextThumbnailSrc;
    }
    buildThumbnailButtons() {
        if (
            !this.el.classList.contains('imageslider__slider') ||
            this.el.querySelector('.slider__thumbnail-buttons') !== null
        ) {
            return;
        }
        this.el.insertAdjacentHTML(
            'beforeend',
            `
            <div class="slider__thumbnail-buttons bg-color">
                <div class="slider__thumbnail-buttons-inner slider__thumbnail-buttons-inner--prev bg-color">
                    <a class="slider__thumbnail-button slider__thumbnail-button--prev slider__thumbnail-button--disabled bg-color" href="#"><div class="thumbnail"></div><div class="arrow border-color"></div></a>
                </div>
                <div class="slider__thumbnail-buttons-inner slider__thumbnail-buttons-inner--next bg-color">
                    <a class="slider__thumbnail-button slider__thumbnail-button--next slider__thumbnail-button--disabled bg-color" href="#"><div class="thumbnail"></div><div class="arrow border-color"></div></a>
                </div>
            </div>
            `
        );
        this.el.querySelector('.slider__thumbnail-button--prev').addEventListener('click', (e) => {
            this.slider.prev();
            e.preventDefault();
        });
        this.el.querySelector('.slider__thumbnail-button--next').addEventListener('click', (e) => {
            this.slider.next();
            e.preventDefault();
        });
        this.updateThumbnailButtons();
    }
    updateThumbnailButtons() {
        if (this.el.querySelector('.slider__thumbnail-buttons') !== null) {
            if (this.getSlideCount() <= this.getSlidesPerPage()) {
                this.el.querySelector('.slider__thumbnail-buttons').style.display = 'none';
            } else {
                this.el.querySelector('.slider__thumbnail-buttons').style.display = 'block';
            }
            if (this.getCurrentIndex() === 0 && !this.hasLoop()) {
                this.el
                    .querySelector('.slider__thumbnail-button--prev')
                    .classList.add('slider__thumbnail-button--disabled');
                this.el.querySelector(
                    '.slider__thumbnail-button--prev .thumbnail'
                ).style.backgroundImage = '';
            } else {
                this.el
                    .querySelector('.slider__thumbnail-button--prev')
                    .classList.remove('slider__thumbnail-button--disabled');
                this.el.querySelector(
                    '.slider__thumbnail-button--prev .thumbnail'
                ).style.backgroundImage = 'url(' + this.getPrevThumbnailSrc() + ')';
            }
            if (
                this.getCurrentIndex() === this.getSlideCount() - this.getSlidesPerPage() &&
                !this.hasLoop()
            ) {
                this.el
                    .querySelector('.slider__thumbnail-button--next')
                    .classList.add('slider__thumbnail-button--disabled');
                this.el.querySelector(
                    '.slider__thumbnail-button--next .thumbnail'
                ).style.backgroundImage = '';
            } else {
                this.el
                    .querySelector('.slider__thumbnail-button--next')
                    .classList.remove('slider__thumbnail-button--disabled');
                this.el.querySelector(
                    '.slider__thumbnail-button--next .thumbnail'
                ).style.backgroundImage = 'url(' + this.getNextThumbnailSrc() + ')';
            }
        }
    }
    buildThumbnailBar() {
        if (
            !this.el.classList.contains('imageslider__slider') ||
            this.el.querySelector('.slider__thumbnail-bar') !== null
        ) {
            return;
        }
        if (this.getSlideCount() <= 1) {
            return;
        }
        this.el.insertAdjacentHTML(
            'beforeend',
            `
                <div class="slider__thumbnail-bar">
                    <div class="slider__thumbnail-bar--inner">
                        <div class="slider__thumbnail-bar--buttons bg-color">
                            <a class="slider__thumbnail-bar-button slider__thumbnail-bar-button--prev bg-color" href="#"><div class="arrow border-color"></div></a>
                        </div>
                        <div class="slider__thumbnail-bar--thumbs bg-color">
                            ${Array(Math.abs(this.getSlideCount() - this.getSlidesPerPage() + 1))
                                .join(0)
                                .split(0)
                                .map(
                                    (item, i) =>
                                        `<a class="slider__thumbnail-item" data-slide="` +
                                        i +
                                        `" href="#" style="background-image: url(` +
                                        this.getThumbnailSrc(i) +
                                        `)"></a>`
                                )
                                .join('')}
                        </div>
                        <div class="slider__thumbnail-bar--buttons bg-color">
                            <a class="slider__thumbnail-bar-button slider__thumbnail-bar-button--next bg-color" href="#"><div class="arrow border-color"></div></a>
                        </div>
                    </div>
                </div>
            `
        );
        if (this.el.querySelector('.slider__thumbnail-item') !== null) {
            this.el.querySelectorAll('.slider__thumbnail-item').forEach((item) => {
                item.addEventListener('click', (e) => {
                    this.goTo(parseInt(e.target.getAttribute('data-slide')));
                    e.preventDefault();
                });
            });
        }
        if (this.el.querySelector('.slider__thumbnail-bar--buttons') !== null) {
            this.el
                .querySelector('.slider__thumbnail-bar-button--prev')
                .addEventListener('click', (e) => {
                    this.slider.prev();
                    e.preventDefault();
                });
            this.el
                .querySelector('.slider__thumbnail-bar-button--next')
                .addEventListener('click', (e) => {
                    this.slider.next();
                    e.preventDefault();
                });
        }
        this.updateThumbnailBar();
    }
    updateThumbnailBar() {
        let thumbnailIndex = this.getCurrentIndex() + 1;
        if (this.el.querySelector('.slider__thumbnail-buttons') !== null) {
            if (this.getSlideCount() <= this.getSlidesPerPage()) {
                this.el.querySelector('.slider__thumbnail-bar').style.display = 'none';
            } else {
                this.el.querySelector('.slider__thumbnail-bar').style.display = 'block';
            }

            // update buttons
            if (this.getCurrentIndex() === 0 && !this.hasLoop()) {
                this.el
                    .querySelector('.slider__thumbnail-bar-button--prev')
                    .classList.add('slider__thumbnail-bar-button--disabled');
            } else {
                this.el
                    .querySelector('.slider__thumbnail-bar-button--prev')
                    .classList.remove('slider__thumbnail-bar-button--disabled');
            }
            if (
                this.getCurrentIndex() === this.getSlideCount() - this.getSlidesPerPage() &&
                !this.hasLoop()
            ) {
                this.el
                    .querySelector('.slider__thumbnail-bar-button--next')
                    .classList.add('slider__thumbnail-bar-button--disabled');
            } else {
                this.el
                    .querySelector('.slider__thumbnail-bar-button--next')
                    .classList.remove('slider__thumbnail-bar-button--disabled');
            }

            // update current thumbnail
            if (this.el.querySelector('.slider__thumbnail-item--current') !== null) {
                this.el
                    .querySelector('.slider__thumbnail-item--current')
                    .classList.remove('slider__thumbnail-item--current');
            }
            if (
                this.el.querySelector(
                    '.slider__thumbnail-item:nth-child(' + (this.getCurrentIndex() + 1) + ')'
                ) !== null
            ) {
                this.el
                    .querySelector(
                        '.slider__thumbnail-item:nth-child(' + (this.getCurrentIndex() + 1) + ')'
                    )
                    .classList.add('slider__thumbnail-item--current');
            }

            // update active thumbnails
            if (this.el.querySelector('.slider__thumbnail-item--active') !== null) {
                this.el.querySelectorAll('.slider__thumbnail-item--active').forEach((el) => {
                    el.classList.remove('slider__thumbnail-item--active');
                });
            }
            if (
                this.el.querySelector(
                    '.slider__thumbnail-item:nth-child(' + thumbnailIndex + ')'
                ) !== null
            ) {
                this.el
                    .querySelector('.slider__thumbnail-item:nth-child(' + thumbnailIndex + ')')
                    .classList.add('slider__thumbnail-item--active');
            }
            if (this.getCurrentIndex() === 0) {
                if (
                    this.el.querySelector(
                        '.slider__thumbnail-item:nth-child(' + (thumbnailIndex + 1) + ')'
                    ) !== null
                ) {
                    this.el
                        .querySelector(
                            '.slider__thumbnail-item:nth-child(' + (thumbnailIndex + 1) + ')'
                        )
                        .classList.add('slider__thumbnail-item--active');
                }
                if (
                    this.el.querySelector(
                        '.slider__thumbnail-item:nth-child(' + (thumbnailIndex + 2) + ')'
                    ) !== null
                ) {
                    this.el
                        .querySelector(
                            '.slider__thumbnail-item:nth-child(' + (thumbnailIndex + 2) + ')'
                        )
                        .classList.add('slider__thumbnail-item--active');
                }
            } else if (this.getCurrentIndex() === this.getSlideCount() - this.getSlidesPerPage()) {
                if (
                    this.el.querySelector(
                        '.slider__thumbnail-item:nth-child(' + (thumbnailIndex - 1) + ')'
                    ) !== null
                ) {
                    this.el
                        .querySelector(
                            '.slider__thumbnail-item:nth-child(' + (thumbnailIndex - 1) + ')'
                        )
                        .classList.add('slider__thumbnail-item--active');
                }
                if (
                    this.el.querySelector(
                        '.slider__thumbnail-item:nth-child(' + (thumbnailIndex - 2) + ')'
                    ) !== null
                ) {
                    this.el
                        .querySelector(
                            '.slider__thumbnail-item:nth-child(' + (thumbnailIndex - 2) + ')'
                        )
                        .classList.add('slider__thumbnail-item--active');
                }
            } else {
                if (
                    this.el.querySelector(
                        '.slider__thumbnail-item:nth-child(' + (thumbnailIndex - 1) + ')'
                    ) !== null
                ) {
                    this.el
                        .querySelector(
                            '.slider__thumbnail-item:nth-child(' + (thumbnailIndex - 1) + ')'
                        )
                        .classList.add('slider__thumbnail-item--active');
                }
                if (
                    this.el.querySelector(
                        '.slider__thumbnail-item:nth-child(' + (thumbnailIndex + 1) + ')'
                    ) !== null
                ) {
                    this.el
                        .querySelector(
                            '.slider__thumbnail-item:nth-child(' + (thumbnailIndex + 1) + ')'
                        )
                        .classList.add('slider__thumbnail-item--active');
                }
            }
        }
    }
    buildButtons() {
        if (this.el.querySelector('.slider__buttons') !== null) {
            return;
        }
        this.el.insertAdjacentHTML(
            'beforeend',
            `
                <div class="slider__buttons">
                    <a class="slider__button slider__button--prev" href="#"><div class="arrow border-color"></div></a>
                    <a class="slider__button slider__button--next" href="#"><div class="arrow border-color"></div></a>
                </div>
            `
        );
        this.el.querySelector('.slider__button--prev').addEventListener('click', (e) => {
            this.slider.prev();
            e.preventDefault();
        });
        this.el.querySelector('.slider__button--next').addEventListener('click', (e) => {
            this.slider.next();
            e.preventDefault();
        });
        this.updateButtons();
    }
    updateButtons() {
        if (this.el.querySelector('.slider__buttons') !== null) {
            if (this.getSlideCount() <= this.getSlidesPerPage()) {
                this.el.querySelector('.slider__buttons').style.display = 'none';
            } else {
                this.el.querySelector('.slider__buttons').style.display = 'block';
            }
            if (this.getCurrentIndex() === 0) {
                this.el
                    .querySelector('.slider__button--prev')
                    .classList.add('slider__button--disabled');
            } else {
                this.el
                    .querySelector('.slider__button--prev')
                    .classList.remove('slider__button--disabled');
            }
            if (this.getCurrentIndex() === this.getSlideCount() - this.getSlidesPerPage()) {
                this.el
                    .querySelector('.slider__button--next')
                    .classList.add('slider__button--disabled');
            } else {
                this.el
                    .querySelector('.slider__button--next')
                    .classList.remove('slider__button--disabled');
            }
        }
    }
    buildDots() {
        if (this.el.querySelector('.slider__dots') !== null) {
            return;
        }
        if (this.getSlideCount() <= 1) {
            return;
        }
        this.el.insertAdjacentHTML(
            'beforeend',
            `
                <div class="slider__dots">
                    ${Array(Math.abs(this.getSlideCount() - this.getSlidesPerPage() + 1))
                        .join(0)
                        .split(0)
                        .map(
                            (item, i) =>
                                `<a class="slider__dot slider-dot-color" data-slide="` +
                                i +
                                `" href="#"></a>`
                        )
                        .join('')}
                </div>
            `
        );
        if (this.el.querySelector('.slider__dot') !== null) {
            this.el.querySelectorAll('.slider__dot').forEach((dot) => {
                dot.addEventListener('click', (e) => {
                    this.goTo(parseInt(e.target.getAttribute('data-slide')));
                    e.preventDefault();
                });
            });
        }
        this.updateDots();
    }
    updateDots() {
        if (this.el.querySelector('.slider__dots') !== null) {
            if (this.el.querySelector('.slider__dot--active') !== null) {
                this.el
                    .querySelector('.slider__dot--active')
                    .classList.remove('slider__dot--active');
            }
            if (
                this.el.querySelector(
                    '.slider__dot:nth-child(' + (this.getCurrentIndex() + 1) + ')'
                ) !== null
            ) {
                this.el
                    .querySelector('.slider__dot:nth-child(' + (this.getCurrentIndex() + 1) + ')')
                    .classList.add('slider__dot--active');
            }
        }
    }
    goTo(i) {
        this.slider.goTo(i);
    }
    initAutoplay() {
        if (this.hasAutoplay()) {
            if (this.interval === null) {
                this.interval = setInterval(() => {
                    if (!this.el.matches(':hover')) {
                        this.slider.next();
                    }
                }, 6000);
            }
        }
    }
    hasLoop() {
        return this.el.hasAttribute('loop');
    }
    hasAutoplay() {
        return !Helper.isInsideEditor() && this.el.hasAttribute('autoplay');
    }
    killSlider() {
        if (this.slider !== null) {
            this.startIndex = this.getCurrentIndex();
            this.destroySlider();
        }
        if (this.el.querySelector('.slider__instance--copy') !== null) {
            this.el.querySelector('.slider__instance--copy').remove();
        }
        if (this.el.querySelector('.slider__buttons') !== null) {
            this.el.querySelector('.slider__buttons').remove();
        }
        if (this.el.querySelector('.slider__dots') !== null) {
            this.el.querySelector('.slider__dots').remove();
        }
        if (this.el.querySelector('.slider__thumbnail-buttons') !== null) {
            this.el.querySelector('.slider__thumbnail-buttons').remove();
        }
        if (this.el.querySelector('.slider__thumbnail-bar') !== null) {
            this.el.querySelector('.slider__thumbnail-bar').remove();
        }
    }
    initializeSlider(selector) {
        this.slider = new Siema({
            selector: selector,
            duration: 300,
            easing: 'ease-out',
            perPage: this.getSlidesPerPage(),
            startIndex: this.startIndex,
            draggable: this.isDraggable(),
            multipleDrag: true,
            threshold: 20,
            loop: this.hasLoop(),
            rtl: false,
            onInit: () => {
                /* https://github.com/pawelgrzybek/siema/issues/71 */
                selector.addEventListener('mousedown', (e) => {
                    this.dragging = e.clientX;
                });
            },
            onChange: () => {
                this.updateButtons();
                this.updateDots();
                this.updateThumbnailButtons();
                this.updateThumbnailBar();
                this.setHeight();
            }
        });
    }
    setHeight() {
        let imgWidth = 0,
            imgHeight = 0,
            containerWidth = 0,
            slideHeight = 0,
            offsetBorder = this.el.hasAttribute('active-border') ? 100 : 0,
            sliderContainer = this.el,
            thisSlideItem = this.el.querySelector(
                '.module__slider-items--auto-height.slider__instance .module__slider-item:nth-child(' +
                    (this.getCurrentIndex() + 1) +
                    ') .image-container--img'
            );
        if (
            this.el.querySelector('.module__slider-items--auto-height.slider__instance') !== null &&
            thisSlideItem !== null
        ) {
            containerWidth = parseInt(sliderContainer.offsetWidth);
            imgWidth = parseInt(thisSlideItem.dataset.width);
            imgHeight = parseInt(thisSlideItem.dataset.height);
            slideHeight =
                ((containerWidth - offsetBorder) / imgWidth) * imgHeight + offsetBorder + 'px';

            this.el.querySelector(
                '.module__slider-items--auto-height.slider__instance--copy'
            ).style.height = slideHeight;
            this.el.querySelector(
                '.module__slider-items--auto-height.slider__instance--copy>div'
            ).style.height = slideHeight;
        }
    }
    updateHeightOnFrontend() {
        var windowWidth = window.innerWidth;
        window.addEventListener('resize', () => {
            var windowWidthNew = window.innerWidth;
            if (windowWidthNew != windowWidth) {
                windowWidth = windowWidthNew;
                this.setHeight();
            }
        });
    }
    destroySlider() {
        this.slider.destroy(true);
        this.slider = null;
    }
    getCurrentIndex() {
        if (this.slider) {
            return this.slider.currentSlide;
        }
        return null;
    }
    getSlideCount() {
        if (this.slider) {
            return this.slider.innerElements.length;
        }
        return 0;
    }
    isDraggable() {
        return !this.el.hasAttribute('disable-dragging');
    }
    getSlidesPerPage() {
        if (document.querySelector('.page-wrap') === null) {
            return 1;
        }
        if (document.querySelector('.page-wrap').clientWidth > 1349) {
            return this.el.hasAttribute('per-page')
                ? parseInt(this.el.getAttribute('per-page'))
                : 1;
        }
        if (document.querySelector('.page-wrap').clientWidth > 992) {
            return this.el.hasAttribute('per-page-lg')
                ? parseInt(this.el.getAttribute('per-page-lg'))
                : 1;
        }
        if (document.querySelector('.page-wrap').clientWidth > 768) {
            return this.el.hasAttribute('per-page-md')
                ? parseInt(this.el.getAttribute('per-page-md'))
                : 1;
        }
        return this.el.hasAttribute('per-page-sm')
            ? parseInt(this.el.getAttribute('per-page-sm'))
            : 1;
    }
    killWidows() {
        // kills widows that can occure in cases where v-if transposes dynamically added childs
        ['.slider__instance--copy', '.slider__buttons', '.slider__dots'].forEach((selector) => {
            if (document.querySelector(selector) !== null) {
                document.querySelectorAll(selector).forEach((el) => {
                    if (
                        el.previousElementSibling === null ||
                        (!el.previousElementSibling.classList.contains('slider__instance') &&
                            !el.previousElementSibling.classList.contains('slider__buttons'))
                    ) {
                        el.remove();
                    }
                });
            }
        });
    }
    setupResizeObserver() {
        if (
            Helper.isInsideEditor() &&
            this.firstSetup === true &&
            document.querySelector('.page-wrap') !== null
        ) {
            const observer = new ResizeObserver(() => {
                this.init(this.el);
            });
            observer.observe(document.querySelector('.page-wrap'));
            this.firstSetup = false;
        }
    }
}
